/**
 * Shared Types and Constants for Smart Trait Components
 */

// ============ Types ============

export type PreviewLanguage = 'zh-TW' | 'en' | 'ja'
export type PreviewSize = 'small' | 'medium' | 'large'

export interface RangeConfig {
  min: number
  max: number
}

export interface DeviceAttributes {
  brightnessRange?: RangeConfig
  colorTemperatureRange?: RangeConfig
  humidityRange?: RangeConfig
  temperatureRange?: RangeConfig
  fanSpeedLevels?: string[]
  supportedModes?: string[]
  customModes?: Array<{ id: string; name: string; icon?: string }>
  customToggles?: Array<{ id: string; name: string; icon?: string }>
  [key: string]: unknown
}

export interface BaseTraitProps {
  deviceSN: string
  style?: string
  size?: PreviewSize
  showLabel?: boolean
  language?: PreviewLanguage
  disabled?: boolean
  className?: string
}

// ============ Labels ============

export const TRAIT_LABELS: Record<PreviewLanguage, Record<string, string>> = {
  'zh-TW': {
    OnOff: '電源',
    Brightness: '亮度',
    ColorTemperature: '色溫',
    ColorSetting: '色溫',
    FanSpeed: '風速',
    Mode: '模式',
    Modes: '模式',
    ThermostatTemperatureSetpoint: '設定溫度',
    TemperatureSetting: '溫度設定',
    TemperatureControl: '溫度控制',
    HumiditySetting: '濕度設定',
    HumidityControl: '濕度控制',
    Timer: '定時',
    CustomToggles: '自定義開關',
    CustomModes: '自定義模式',
    Toggles: '開關組',
    ThermostatMode: '空調模式',
    OpenClose: '開合控制',
    LockUnlock: '鎖定',
    StartStop: '啟動/停止',
    OnOffToggle: '開關切換',
    Sensor: '感測器',
    Detector: '偵測器',
    Setpoints: '設定點',
  },
  'en': {
    OnOff: 'Power',
    Brightness: 'Brightness',
    ColorTemperature: 'Color Temp',
    ColorSetting: 'Color Temp',
    FanSpeed: 'Fan Speed',
    Mode: 'Mode',
    Modes: 'Mode',
    ThermostatTemperatureSetpoint: 'Set Temp',
    TemperatureSetting: 'Temperature',
    TemperatureControl: 'Temp Control',
    HumiditySetting: 'Humidity',
    HumidityControl: 'Humidity',
    Timer: 'Timer',
    CustomToggles: 'Toggles',
    CustomModes: 'Modes',
    Toggles: 'Toggles',
    ThermostatMode: 'Thermostat',
    OpenClose: 'Open/Close',
    LockUnlock: 'Lock',
    StartStop: 'Start/Stop',
    OnOffToggle: 'Toggle',
    Sensor: 'Sensor',
    Detector: 'Detector',
    Setpoints: 'Setpoints',
  },
  'ja': {
    OnOff: '電源',
    Brightness: '明るさ',
    ColorTemperature: '色温度',
    ColorSetting: '色温度',
    FanSpeed: '風量',
    Mode: 'モード',
    Modes: 'モード',
    ThermostatTemperatureSetpoint: '設定温度',
    TemperatureSetting: '温度設定',
    TemperatureControl: '温度制御',
    HumiditySetting: '湿度設定',
    HumidityControl: '湿度制御',
    Timer: 'タイマー',
    CustomToggles: 'カスタム切替',
    CustomModes: 'カスタムモード',
    Toggles: 'スイッチ',
    ThermostatMode: '空調モード',
    OpenClose: '開閉',
    LockUnlock: 'ロック',
    StartStop: '運転/停止',
    OnOffToggle: 'トグル',
    Sensor: 'センサー',
    Detector: '検知器',
    Setpoints: '設定値',
  },
}

export const ONOFF_STATUS_LABELS: Record<PreviewLanguage, { on: string; off: string }> = {
  'zh-TW': { on: '開啟', off: '關閉' },
  'en': { on: 'ON', off: 'OFF' },
  'ja': { on: 'オン', off: 'オフ' },
}

export const COLOR_TEMP_LABELS: Record<PreviewLanguage, { warm: string; daylight: string; cool: string }> = {
  'zh-TW': { warm: '暖光', daylight: '日光', cool: '冷光' },
  'en': { warm: 'Warm', daylight: 'Daylight', cool: 'Cool' },
  'ja': { warm: '暖色', daylight: '昼光', cool: '寒色' },
}

export const FAN_SPEED_CONFIG: Record<string, { label: Record<PreviewLanguage, string>; icon: string }> = {
  auto: { label: { 'zh-TW': '自動', 'en': 'Auto', 'ja': '自動' }, icon: 'hdr_auto' },
  low: { label: { 'zh-TW': '低速', 'en': 'Low', 'ja': '弱' }, icon: 'air' },
  medium: { label: { 'zh-TW': '中速', 'en': 'Medium', 'ja': '中' }, icon: 'mode_fan' },
  high: { label: { 'zh-TW': '高速', 'en': 'High', 'ja': '強' }, icon: 'mode_fan' },
  turbo: { label: { 'zh-TW': '強力', 'en': 'Turbo', 'ja': 'ターボ' }, icon: 'storm' },
  silent: { label: { 'zh-TW': '靜音', 'en': 'Silent', 'ja': '静音' }, icon: 'volume_off' },
  sleep: { label: { 'zh-TW': '睡眠', 'en': 'Sleep', 'ja': '睡眠' }, icon: 'bedtime' },
}

export const MODE_CONFIG: Record<string, { label: Record<PreviewLanguage, string>; icon: string; color: string }> = {
  auto: { label: { 'zh-TW': '自動', 'en': 'Auto', 'ja': '自動' }, icon: 'hdr_auto', color: '#38bdf8' },
  cool: { label: { 'zh-TW': '冷氣', 'en': 'Cool', 'ja': '冷房' }, icon: 'ac_unit', color: '#38bdf8' },
  heat: { label: { 'zh-TW': '暖氣', 'en': 'Heat', 'ja': '暖房' }, icon: 'local_fire_department', color: '#f97316' },
  'fan-only': { label: { 'zh-TW': '送風', 'en': 'Fan', 'ja': '送風' }, icon: 'air', color: '#94a3b8' },
  dry: { label: { 'zh-TW': '除濕', 'en': 'Dry', 'ja': '除湿' }, icon: 'water_drop', color: '#06b6d4' },
  eco: { label: { 'zh-TW': '節能', 'en': 'Eco', 'ja': 'エコ' }, icon: 'eco', color: '#22c55e' },
  sleep: { label: { 'zh-TW': '睡眠', 'en': 'Sleep', 'ja': '睡眠' }, icon: 'bedtime', color: '#a78bfa' },
  turbo: { label: { 'zh-TW': '強力', 'en': 'Turbo', 'ja': 'ターボ' }, icon: 'bolt', color: '#facc15' },
}

export const THERMOSTAT_MODE_CONFIG: Record<string, { label: Record<PreviewLanguage, string>; icon: string; color: string }> = {
  auto: { label: { 'zh-TW': '自動', 'en': 'Auto', 'ja': '自動' }, icon: 'hdr_auto', color: '#38bdf8' },
  cool: { label: { 'zh-TW': '冷氣', 'en': 'Cool', 'ja': '冷房' }, icon: 'ac_unit', color: '#38bdf8' },
  heat: { label: { 'zh-TW': '暖氣', 'en': 'Heat', 'ja': '暖房' }, icon: 'local_fire_department', color: '#f97316' },
  dry: { label: { 'zh-TW': '除濕', 'en': 'Dry', 'ja': '除湿' }, icon: 'water_drop', color: '#06b6d4' },
  'fan-only': { label: { 'zh-TW': '送風', 'en': 'Fan', 'ja': '送風' }, icon: 'air', color: '#94a3b8' },
  fan: { label: { 'zh-TW': '送風', 'en': 'Fan', 'ja': '送風' }, icon: 'air', color: '#94a3b8' },
  off: { label: { 'zh-TW': '關閉', 'en': 'Off', 'ja': 'オフ' }, icon: 'power_settings_new', color: '#94a3b8' },
}

export const CUSTOM_TOGGLE_CONFIG: Record<string, { label: Record<PreviewLanguage, string>; icon: string }> = {
  eco: { label: { 'zh-TW': '節能', 'en': 'Eco', 'ja': 'エコ' }, icon: 'eco' },
  sleep: { label: { 'zh-TW': '睡眠', 'en': 'Sleep', 'ja': '睡眠' }, icon: 'bedtime' },
  turbo: { label: { 'zh-TW': '強力', 'en': 'Turbo', 'ja': 'ターボ' }, icon: 'bolt' },
  quiet: { label: { 'zh-TW': '靜音', 'en': 'Quiet', 'ja': '静音' }, icon: 'volume_off' },
  swing: { label: { 'zh-TW': '擺風', 'en': 'Swing', 'ja': '首振り' }, icon: 'sync_alt' },
  timer: { label: { 'zh-TW': '定時', 'en': 'Timer', 'ja': 'タイマー' }, icon: 'timer' },
}

// ============ Default Ranges ============

export const DEFAULT_RANGES = {
  brightness: { min: 0, max: 100 },
  colorTemperature: { min: 2700, max: 6500 },
  temperature: { min: 16, max: 30 },
  humidity: { min: 30, max: 80 },
  openClose: { min: 0, max: 100 },
}

// ============ Timer Options ============

export const TIMER_OPTIONS = [15, 30, 60, 120]

export function formatTimerLabel(minutes: number): string {
  return minutes >= 60 ? `${minutes / 60}h` : `${minutes}m`
}

// ============ Helper Functions ============

export function getTraitLabel(traitName: string, language: PreviewLanguage = 'zh-TW'): string {
  return TRAIT_LABELS[language][traitName] || traitName
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

/**
 * Calculate color based on temperature (warm orange -> neutral white -> cool blue)
 */
export function getColorTempColor(percent: number): string {
  if (percent <= 0.5) {
    const r = 255
    const g = Math.round(180 + percent * 150)
    const b = Math.round(100 + percent * 310)
    return `rgb(${r}, ${g}, ${b})`
  } else {
    const t = (percent - 0.5) * 2
    const r = Math.round(255 - t * 60)
    const g = Math.round(255 - t * 30)
    const b = 255
    return `rgb(${r}, ${g}, ${b})`
  }
}
